<script setup>
import { ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentSeatLimitState from './AgentSeatLimitState.vue';
import AgentRoleFields from './AgentRoleFields.vue';
import { rolePayload } from './agentRole';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmail = ref('');
const role = ref('agent');
const customRoleId = ref(null);
// Preenchido quando o servidor recusa por limite de assento (402). Enquanto
// nao nulo, a tela troca o formulario pelo estado de limite em vez de deixar
// a pessoa tentar de novo contra o mesmo teto.
const seatLimit = ref(null);

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  role: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  role,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    // Perfil de acesso vale sobre o papel agent; o backend tambem forca isso.
    const roleFields = rolePayload({
      role: role.value,
      customRoleId: customRoleId.value,
    });
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
      role: roleFields.role,
    };
    if (roleFields.custom_role_id) {
      payload.custom_role_id = roleFields.custom_role_id;
    }

    await store.dispatch('agents/create', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const {
      response: {
        status,
        data: {
          error: errorResponse = '',
          error_code: errorCode = '',
          seats = null,
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    if (status === 402 && errorCode === 'agent_limit_exceeded') {
      seatLimit.value = {
        used: seats?.used ?? 0,
        total: seats?.total ?? 0,
      };
      return;
    }

    let errorMessage = '';
    if (status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <AgentSeatLimitState
      v-if="seatLimit"
      :used="seatLimit.used"
      :total="seatLimit.total"
    >
      <template #actions>
        <Button
          faded
          slate
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click="emit('close')"
        />
      </template>
    </AgentSeatLimitState>
    <form
      v-else
      class="flex flex-col items-start w-full"
      @submit.prevent="addAgent"
    >
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <AgentRoleFields
        v-model:role="role"
        v-model:custom-role-id="customRoleId"
        :custom-roles="getCustomRoles"
        :has-error="v$.role.$error"
      />

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
